import * as React from 'react';
import { Users, Play, X, Plus, Minus, Flag, Check, Circle } from 'lucide-react';
import { Button } from './ui/Button';
import IgygRepBlocksPage from './IgygRepBlocksPage';
import { useSwipeTabs } from '../hooks/useSwipeTabs';
import { useLocale } from '../context/LocaleContext';
import { useStartedTraining, type PartnerSessionCtx, type PlannedTrainingDTO } from '../context/StartedTrainingContext';
import { serverNow, syncServerClock } from '../lib/clockSync';
import {
  joinPartnerChannel,
  type PartnerChannelHandle,
  type PartnerReps,
} from '../lib/partnerSession';

/**
 * Two-device "I Go You Go" (IGYG) partner session — a self-contained live view
 * (separate from the solo StartedTrainingCard). Both partners share:
 *  - one timer anchored to a server-clock `startedAtServerMs` broadcast by the host,
 *  - a combined rep counter per exercise (each partner adds their own reps freely),
 *  - presence (who's connected).
 * Host-anchored + ephemeral (see lib/partnerSession.ts). No backend.
 */

type Props = { training: PlannedTrainingDTO; partner: PartnerSessionCtx };

function fmtTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function PartnerStartedCard({ training, partner }: Props) {
  const { t } = useLocale();
  const { stopTraining } = useStartedTraining();
  const isHost = partner.role === 'host';
  const selfUserId = partner.selfUserId;

  // ── Derive the IGYG block + its exercises/targets from the training ──
  const { blockTitle, durationMin, isAmrap, exercises } = React.useMemo(() => {
    const blocks: any[] = Array.isArray((training as any)?.plannerMeta?.blocks)
      ? (training as any).plannerMeta.blocks
      : [];
    // Prefer the exact block the host invited from (a training can have >1 IGYG
    // block); fall back to the first IGYG block, then the first block.
    const wantId = (training as any)?.igygBlockId;
    const block =
      (wantId && blocks.find((b) => b?.id === wantId)) ||
      blocks.find((b) => b?.partnerMode === 'igyg') ||
      blocks[0] ||
      null;
    const dMin = Number(block?.methodMeta?.durationMin) || null;
    const amrap = block?.methodMeta?.mode === 'amrap' && !!dMin;
    const ids: string[] = Array.isArray(block?.exerciseIds) && block.exerciseIds.length
      ? block.exerciseIds
      : (training.exercises ?? []).map((e) => e.id).filter(Boolean);
    const byId = new Map((training.exercises ?? []).map((e) => [e.id, e]));
    const list = ids.map((id) => {
      const e: any = byId.get(id);
      const reps = Array.isArray(e?.repsPerSet) ? e.repsPerSet : null;
      const target = reps ? reps.reduce((a: number, b: any) => a + (Number(b) || 0), 0) : null;
      return { id, name: (e?.nameRaw ?? e?.raw ?? id) as string, target: target || null };
    });
    return { blockTitle: (training.title ?? '') as string, durationMin: dMin, isAmrap: amrap, exercises: list };
  }, [training]);

  // ── Live state ──
  const [startedAtServerMs, setStartedAtServerMs] = React.useState<number | null>(null);
  const [reps, setReps] = React.useState<PartnerReps>({});
  const [members, setMembers] = React.useState<Array<{ userId: string; name: string }>>([]);
  const [finished, setFinished] = React.useState(false);
  const [now, setNow] = React.useState(() => serverNow());

  // Two swipeable pages: the fine +/- counters and the big "knock down" blocks.
  const [page, setPage] = React.useState<'counter' | 'blocks'>('counter');
  const pageSwipe = useSwipeTabs(['counter', 'blocks'] as const, page, setPage);

  // Refs so the (once-wired) channel handlers can read the latest values.
  const startedRef = React.useRef<number | null>(null);
  const repsRef = React.useRef<PartnerReps>({});
  const finishedRef = React.useRef(false);
  const handleRef = React.useRef<PartnerChannelHandle | null>(null);
  startedRef.current = startedAtServerMs;
  repsRef.current = reps;
  finishedRef.current = finished;

  const broadcastState = React.useCallback(() => {
    handleRef.current?.sendState({
      training,
      startedAtServerMs: startedRef.current,
      reps: repsRef.current,
      finished: finishedRef.current,
    });
  }, [training]);

  // ── Join the partner channel once ──
  React.useEffect(() => {
    void syncServerClock();
    const handle = joinPartnerChannel(
      partner.sessionId,
      { userId: selfUserId, name: partner.selfName },
      {
        onReady: () => { if (!isHost) handle.sendHello(); },
        onHello: () => { if (isHost) broadcastState(); },
        onState: (s) => {
          if (isHost) return; // host is authoritative
          if (typeof s.startedAtServerMs === 'number') setStartedAtServerMs(s.startedAtServerMs);
          if (s.reps) setReps(s.reps);
          if (s.finished) setFinished(true);
        },
        onRep: ({ userId, exerciseId, delta }) => {
          if (userId === selfUserId) return; // our own taps are applied optimistically
          setReps((prev) => {
            const ex = { ...(prev[exerciseId] || {}) };
            ex[userId] = (ex[userId] || 0) + delta;
            return { ...prev, [exerciseId]: ex };
          });
        },
        onFinish: () => setFinished(true),
        onPresence: (m) => setMembers(m),
      },
    );
    handleRef.current = handle;
    return () => { handle.leave(); handleRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner.sessionId]);

  // ── Tick the timer while running ──
  React.useEffect(() => {
    if (startedAtServerMs == null || finished) return;
    const id = window.setInterval(() => setNow(serverNow()), 250);
    return () => window.clearInterval(id);
  }, [startedAtServerMs, finished]);

  // ── Actions ──
  const startNow = () => {
    const at = serverNow();
    setStartedAtServerMs(at);
    startedRef.current = at;
    handleRef.current?.sendState({ training, startedAtServerMs: at, reps: repsRef.current, finished: false });
  };

  const addRep = (exerciseId: string, delta: number) => {
    setReps((prev) => {
      const ex = { ...(prev[exerciseId] || {}) };
      const next = Math.max(0, (ex[selfUserId] || 0) + delta);
      ex[selfUserId] = next;
      return { ...prev, [exerciseId]: ex };
    });
    handleRef.current?.sendRep({ userId: selfUserId, exerciseId, delta });
  };

  const finish = () => {
    setFinished(true);
    handleRef.current?.sendFinish();
  };

  const close = () => { handleRef.current?.leave(); stopTraining(); };

  // ── Derived display ──
  const started = startedAtServerMs != null;
  const elapsedMs = started ? Math.max(0, now - startedAtServerMs) : 0;
  const amrapCapMs = isAmrap && durationMin ? durationMin * 60 * 1000 : null;
  const timeUp = amrapCapMs != null && elapsedMs >= amrapCapMs;
  const timerMs = amrapCapMs != null ? Math.max(0, amrapCapMs - elapsedMs) : elapsedMs;

  const combinedFor = (exId: string) =>
    Object.values(reps[exId] || {}).reduce((a, b) => a + b, 0);
  const partnerId = members.find((m) => m.userId !== selfUserId)?.userId ?? null;
  const partnerName = partner.partnerName || members.find((m) => m.userId !== selfUserId)?.name || t('Partner', 'Partner');
  const selfOnline = members.some((m) => m.userId === selfUserId);
  const partnerOnline = !!partnerId;

  const grandTotal = exercises.reduce((a, e) => a + combinedFor(e.id), 0);

  return (
    <div
      className="fixed inset-0 z-[1000] flex flex-col bg-white dark:bg-slate-950"
      style={{ paddingTop: 'var(--safe-top, 0px)', paddingBottom: 'var(--safe-bottom, 0px)' }}
    >
      <div className="relative w-full flex-1 min-h-0 overflow-hidden flex flex-col bg-white dark:bg-slate-950">
        {/* ── Header ── */}
        <div className="px-4 py-3 flex items-center gap-3 shrink-0 border-b border-gray-200/60 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.03]">
          <Users size={18} className="text-brand-600 dark:text-brand-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{blockTitle || t('Workout', 'Workout')}</div>
            <div className="text-[11px] opacity-40 truncate">{t('I Go You Go', 'I Go You Go')}</div>
          </div>
          <button type="button" onClick={close} aria-label={t('Close', 'Close')} className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10 transition text-gray-500 dark:text-gray-400">
            <X size={18} />
          </button>
        </div>

        {/* ── Body (swipe between the fine counters and the knock-down blocks) ── */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden" ref={pageSwipe.ref} onTouchStart={pageSwipe.onTouchStart} onTouchEnd={pageSwipe.onTouchEnd}>
          {!finished && page === 'blocks' ? (
            <IgygRepBlocksPage exercises={exercises} combinedFor={combinedFor} onKnockDown={addRep} disabled={!started} t={t} />
          ) : (
          <div className="flex flex-col gap-2 px-4 pb-4 pt-3 min-h-full">

            {/* Presence */}
            <div className="flex items-center justify-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <Circle size={9} className={selfOnline ? 'text-emerald-500 fill-emerald-500' : 'text-gray-300 fill-gray-300'} />
                {partner.selfName} <span className="opacity-40">({t('you', 'you')})</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Circle size={9} className={partnerOnline ? 'text-emerald-500 fill-emerald-500' : 'text-gray-300 fill-gray-300 animate-pulse'} />
                {partnerName} {!partnerOnline && <span className="opacity-40">({t('waiting…', 'waiting…')})</span>}
              </span>
            </div>

            {finished ? (
              /* ── Summary ── */
              <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 p-6 flex flex-col items-center justify-center text-center gap-4 mt-1">
                <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/40 grid place-items-center text-emerald-600 dark:text-emerald-400">
                  <Check size={28} />
                </div>
                <div className="text-lg font-bold">{t('Workout complete!', 'Workout complete!')}</div>
                <div className="text-sm opacity-60">{t('Combined reps', 'Combined reps')}: <span className="font-semibold">{grandTotal}</span> · {t('Time', 'Time')}: {fmtTime(elapsedMs)}</div>
                <div className="w-full max-w-sm mt-2 space-y-1">
                  {exercises.map((e) => (
                    <div key={e.id} className="flex items-center justify-between text-sm px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/[0.04]">
                      <span className="truncate">{e.name}</span>
                      <span className="font-semibold tabular-nums">{combinedFor(e.id)}{e.target ? ` / ${e.target}` : ''}</span>
                    </div>
                  ))}
                </div>
                <Button variant="primary" className="mt-2" onClick={close}>{t('Done', 'Done')}</Button>
              </div>
            ) : (
              <>
                {/* ── Timer card ── */}
                <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 overflow-hidden">
                  <div className="relative px-4 pt-3 pb-4 text-center">
                    <div className="flex justify-center mb-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                        <Users size={11} /> {t('I Go You Go', 'I Go You Go')}
                      </span>
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest opacity-40 mb-1.5">
                      {amrapCapMs != null ? t('AMRAP Countdown', 'AMRAP Countdown') : t('Workout Timer', 'Workout Timer')}
                    </div>
                    <div className={['text-6xl sm:text-7xl font-black tabular-nums tracking-tight leading-none', timeUp ? 'text-rose-500' : ''].join(' ')}>
                      {started ? fmtTime(timerMs) : '0:00'}
                    </div>
                    {!started && (
                      isHost ? (
                        <Button variant="success" className="mt-4" onClick={startNow}>
                          <Play size={16} /> {t('Start', 'Start')}
                        </Button>
                      ) : (
                        <div className="mt-4 text-sm opacity-60">{t('Waiting for host to start…', 'Waiting for host to start…')}</div>
                      )
                    )}
                  </div>
                </div>

                {/* ── Combined rep counters ── */}
                {exercises.map((e) => {
                  const combined = combinedFor(e.id);
                  const mine = reps[e.id]?.[selfUserId] || 0;
                  const theirs = partnerId ? (reps[e.id]?.[partnerId] || 0) : Math.max(0, combined - mine);
                  return (
                    <div key={e.id} className="rounded-2xl border border-gray-200/60 dark:border-white/10 p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-medium text-sm truncate">{e.name}</div>
                        <div className="text-xs opacity-50 tabular-nums">
                          {combined}{e.target ? ` / ${e.target}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => addRep(e.id, -1)}
                          disabled={!started || mine <= 0}
                          className="h-11 w-11 rounded-full border-2 border-brand-300 dark:border-brand-700 text-xl font-bold text-brand-700 dark:text-brand-300 grid place-items-center disabled:opacity-30 active:scale-95 transition"
                          aria-label={t('Remove one', 'Remove one')}
                        >
                          <Minus size={18} />
                        </button>
                        <div className="flex-1 text-center">
                          <div className="text-3xl font-black tabular-nums leading-none">{combined}</div>
                          <div className="text-[11px] opacity-50 mt-1">
                            {t('You', 'You')} {mine} · {partnerName} {theirs}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => addRep(e.id, 1)}
                          disabled={!started}
                          className="h-11 w-11 rounded-full bg-brand-500 text-white text-xl font-bold grid place-items-center disabled:opacity-30 active:scale-95 transition"
                          aria-label={t('Add one', 'Add one')}
                        >
                          <Plus size={18} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {exercises.length === 0 && (
                  <div className="text-sm opacity-60 px-3 py-6 text-center">{t('No exercises in this block.', 'No exercises in this block.')}</div>
                )}
              </>
            )}
          </div>
          )}
        </div>

        {/* ── Page dots (only while running) ── */}
        {!finished && (
          <div className="shrink-0 flex justify-center gap-2 pt-1.5">
            {(['counter', 'blocks'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                aria-label={p === 'counter' ? t('Counters', 'Counters') : t('Knock down reps', 'Knock down reps')}
                aria-current={page === p ? 'page' : undefined}
                className={['rounded-full transition-all', page === p ? 'w-2 h-2 bg-slate-700 dark:bg-slate-200' : 'w-1.5 h-1.5 bg-slate-400/60 dark:bg-slate-500/60'].join(' ')}
              />
            ))}
          </div>
        )}

        {/* ── Finish footer ── */}
        {!finished && (
          <div className="p-3 shrink-0 border-t border-gray-200/60 dark:border-white/10">
            <Button variant="primary" className="w-full justify-center" onClick={finish} disabled={!started}>
              <Flag size={16} /> {t('Finish Workout', 'Finish Workout')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PartnerStartedCard;
